import { Injectable, inject } from '@angular/core';
import type { InstantaneCatalogue, StockageCatalogue } from '@stockflow/offline-core';
import { AuthService } from '../services/auth.service';
import { proprietaireCourant } from '../services/proprietaire-courant';
import { BaseLocale } from './base-locale';

/**
 * LE CATALOGUE HORS LIGNE, DANS LA BASE LOCALE CHIFFREE PAR LE PIN.
 *
 * Il était dans `localStorage` (en clair), puis chiffré par la clé d'appareil. Il est
 * maintenant chiffré par la CLE DE DONNEES : sans PIN saisi, il est illisible — y
 * compris pour un script de la page. Clients, prix, stock : de quoi valoir ce soin.
 *
 * Verrouillé (rechargement en ligne sans PIN, verrou), le catalogue n'est ni lu ni
 * écrit : la caisse le recharge depuis le serveur, sans le garder. Hors ligne, il
 * faut le PIN de toute façon pour ouvrir une session.
 *
 * Une base par utilisateur ET par entreprise (voir `nomBase`) : le catalogue d'une
 * autre entreprise ne peut pas être servi.
 */
@Injectable({ providedIn: 'root' })
export class StockageCatalogueRxdb implements StockageCatalogue {
    private readonly base = inject(BaseLocale);
    private readonly auth = inject(AuthService);

    private async proprietaire(companyId: number) {
        const courant = await proprietaireCourant(this.auth).catch(() => null);
        return courant ? { email: courant.email, companyId: String(companyId) } : null;
    }

    private nom(companyId: number): string {
        return `catalogue:${companyId}`;
    }

    async lire(companyId: number): Promise<InstantaneCatalogue | null> {
        try {
            const p = await this.proprietaire(companyId);
            const instantane = p ? await this.base.lire<InstantaneCatalogue>(p, this.nom(companyId)) : null;
            // Un instantané d'une autre entreprise est ignoré plutôt que servi.
            return instantane && instantane.companyId === companyId && instantane.recuA ? instantane : null;
        } catch {
            return null;
        }
    }

    async ecrire(instantane: InstantaneCatalogue): Promise<void> {
        try {
            const p = await this.proprietaire(instantane.companyId);
            if (p) await this.base.ecrire(p, this.nom(instantane.companyId), instantane);
        } catch {
            // Le catalogue se recharge : une écriture qui échoue ne casse pas la caisse.
        }
    }

    async vider(companyId: number): Promise<void> {
        try {
            const p = await this.proprietaire(companyId);
            if (p) await this.base.supprimer(p, this.nom(companyId));
        } catch {
            /* idem */
        }
    }
}
